/**
 * "Excedente de fibra" de un TRASLADO.
 *
 * Cuando el técnico agrega FIBRA PLANA en los materiales registra los metros de
 * excedente. Los primeros metros son gratis (catálogo, 600 por defecto); si los
 * supera, Activaciones valida el excedente, define precio y forma de pago y lo
 * aprueba. Mientras esté PENDIENTE el técnico no puede finalizar.
 *
 * Vive en `agendamiento.observaciones.excedente_fibra`, pero esa clave SOLO la
 * escribe el backend (endpoint `orden-trabajo/<id>/excedente-fibra/…`): ahí se
 * calculan los montos y el PUT de agendamiento la conserva. No incluirla a mano
 * en un PUT de observaciones: no tendría efecto.
 */
export type EstadoExcedenteFibra = "SIN_COBRO" | "PENDIENTE" | "APROBADO";

export type ExcedenteFibra = {
  /** Metros de excedente registrados por el técnico. */
  metros: number;
  metros_gratis: number;
  /** Metros que superan los gratis (por metro o fracción). */
  metros_cobrados: number;
  precio_metro_sin_iva: number;
  iva_porcentaje?: number;
  valor_sin_iva: number;
  iva: number;
  valor_total: number;
  /** Forma de pago y meses: los define Activaciones al aprobar. */
  tipo_pago?: string | null;
  meses_pago?: number | null;
  estado: EstadoExcedenteFibra;
  registrado_por?: string;
  registrado_fecha?: string;
  aprobado_por?: string;
  aprobado_fecha?: string;
};

/** Excedente guardado en unas observaciones de agendamiento (null si no hay). */
export function excedenteFibraDe(obs: any): ExcedenteFibra | null {
  let o = obs;
  if (typeof o === "string") {
    try {
      o = JSON.parse(o);
    } catch {
      o = null;
    }
  }
  const exc = o && typeof o === "object" ? o.excedente_fibra : null;
  return exc && typeof exc === "object" ? (exc as ExcedenteFibra) : null;
}

/** El excedente supera los metros gratis y Activaciones aún no lo aprueba. */
export function excedenteFibraPendiente(exc?: ExcedenteFibra | null): boolean {
  return !!exc && `${exc.estado || ""}`.toUpperCase() === "PENDIENTE";
}

/** Códigos de inventario de la fibra plana (respaldo si no viene la descripción). */
const CODIGOS_FIBRA_PLANA = ["MFO.015.0046", "MFO.015.0082"];

/**
 * ¿El material es FIBRA PLANA? Igual que la detección de CABLE UTP: por la
 * descripción del producto (FIBRA PLANA 1 hilo / 2 hilos) o por su código.
 */
export function esFibraPlana(material: any): boolean {
  const desc = `${material?.descripcion || material?.producto_data?.descripcion || material?.nombre || ""}`.toUpperCase();
  const cod = `${material?.codigo || material?.producto_data?.codigo || ""}`.toUpperCase();
  return desc.includes("FIBRA PLANA") || desc.includes("FIBRA PLATA") || CODIGOS_FIBRA_PLANA.includes(cod);
}

/** ¿Hay FIBRA PLANA entre los materiales del traslado? */
export function tieneFibraPlana(...listas: any[]): boolean {
  return listas.some((lista) => Array.isArray(lista) && lista.some(esFibraPlana));
}

const num = (v: any) => {
  const n = Number(`${v ?? ""}`.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Metros de un material de fibra, según cómo venga el registro:
 *  - en edición (store del técnico): `producto_factor[]` con la "Cantidad"
 *    (metros) que se escribe al seleccionar cada serie;
 *  - ya guardado en la OT: `cantidad_metros` (total) o `series_metros` (por serie);
 *  - sin series cargadas pero con `usa_metros`: la cantidad escrita.
 */
export function metrosDeMaterial(material: any): number {
  if (!material) return 0;
  const factor = Array.isArray(material.producto_factor) ? material.producto_factor : [];
  if (factor.length > 0) {
    return factor.reduce((acc: number, f: any) => acc + num(f?.cantidad_metros), 0);
  }
  if (material.cantidad_metros != null && `${material.cantidad_metros}` !== "") {
    return num(material.cantidad_metros);
  }
  const porSerie = material.series_metros;
  if (porSerie && typeof porSerie === "object") {
    const total = Object.values(porSerie).reduce((acc: number, v: any) => acc + num(v), 0);
    if (total > 0) return total;
  }
  if (material.usa_metros) return num(material.cantidad);
  return 0;
}

/**
 * Metros de FIBRA PLANA cargados en los materiales del traslado ("fibra
 * utilizada"). Toma la primera lista que tenga fibra (store del técnico antes
 * que el form) para no sumar dos copias del mismo material.
 */
export function metrosFibraPlana(...listas: any[]): number {
  for (const lista of listas) {
    if (!Array.isArray(lista)) continue;
    const fibras = lista.filter(esFibraPlana);
    if (fibras.length === 0) continue;
    const total = fibras.reduce((acc: number, m: any) => acc + metrosDeMaterial(m), 0);
    return Math.round(total * 100) / 100;
  }
  return 0;
}

export const moneyExcedente = (n?: number | null) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
